// =============================================
// App.js - Universal Background Color Update
//
// This update changes the universal background color to gray.100 (light mode) and gray.900 (dark mode)
// for a lighter and consistent look across the app. Removes unused colorMode variable.
// All other functionality remains unchanged.
//
// Migrated to Chakra UI v3: ChakraProvider → Provider (with custom system),
// useColorModeValue from snippet's color-mode, theme → system.
//
// Last updated: 2026-06-10T00:00:00Z
// =============================================

import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  VStack,
  useDisclosure,
  useBreakpointValue,
  Center,
} from '@chakra-ui/react';
import { Provider } from './components/ui/provider';
import { useColorModeValue } from './components/ui/color-mode';
import PageLoader from './components/ui/PageLoader';
import { Toaster } from './components/ui/toaster';
import { AuthProvider, useAuthContext } from "./AuthProvider";
import { ShoppingCartProvider } from "./ShoppingCartContext";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { auth, db } from "./firebase";
import { signOut } from "firebase/auth";
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import system from "./theme";
import Footer from "./Footer";
import "./App.css";
// TODO: Re-enable CookieConsent once test environment issue is resolved
// import { CookieConsent } from './components/CookieConsent2';
// import TestPasswordReset from "./test";

const TestList = lazy(() => import("./TestList"));

const Questions = lazy(() => import("./Questions"));
const ExamModeSelect = lazy(() => import("./ExamModeSelect"));
const UserProfile = lazy(() => import("./UserProfile"));
const UserRegistration = lazy(() => import("./UserRegistration"));
const Login = lazy(() => import("./Login"));
const EditQuestions = lazy(() => import("./EditQuestions"));
const MyTestList = lazy(() => import("./MyTestList"));
const TestResults = lazy(() => import("./TestResults"));
const Exams = lazy(() => import("./Exams"));
const CustomizingExam = lazy(() => import("./CustomizingExam"));
const PermissionError = lazy(() => import("./PermissionError"));
const EditSingleQuestions = lazy(() => import("./EditSingleQuestions"));
const Dashboard = lazy(() => import("./Dashboard"));
const ExamGenerator = lazy(() => import("./ExamGenerator"));
const Payment = lazy(() => import("./Payment"));
const PaymentSuccess = lazy(() => import("./PaymentSuccess"));
const Subscriptions = lazy(() => import("./Subscriptions"));
const PasswordReset = lazy(() => import("./PasswordReset"));
const Privacy = lazy(() => import('./Privacy'));
const Terms = lazy(() => import('./Terms'));
const FunctionLog = lazy(() => import('./FunctionLog'));
const RagSystem = lazy(() => import('./RagSystem'));
const AdminHelp = lazy(() => import('./AdminHelp'));
const LandingPageManager = lazy(() => import('./LandingPageManager'));
const AdminUsers = lazy(() => import('./AdminUsers'));
const PBQReview = lazy(() => import('./PBQReview'));
const DragAndDropMatch = lazy(() => import('./components/DragAndDropMatch'));
const Cart = lazy(() => import('./Cart'));
const Checkout = lazy(() => import('./Checkout'));
const SimpleCheckout = lazy(() => import('./SimpleCheckout'));
const PaymentLinkCheckout = lazy(() => import('./PaymentLinkCheckout'));
const CookieConsent = lazy(() =>
  import('./components/CookieConsent2').then((module) => ({ default: module.CookieConsent }))
);

// ScrollToTop component
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

function RouteLoader() {
  return <PageLoader />;
}

// Lightweight homepage skeleton shown while TestList chunk loads
function HomeSkeleton() {
  const bg = useColorModeValue("gray.100", "gray.900");
  const skeletonBg = useColorModeValue("gray.200", "gray.700");
  const pulseAnim = { animation: 'eg-skeleton-pulse 1.5s ease-in-out infinite' };
  return (
    <Box bg={bg} minHeight="100vh">
      <Box px={{ base: 4, md: 8, lg: 16 }} py={8} maxWidth="1200px" mx="auto">
        {/* Hero skeleton */}
        <Box
          mb={10}
          borderRadius="xl"
          bg={skeletonBg}
          height={{ base: "200px", md: "300px", lg: "380px" }}
          style={pulseAnim}
        />
        {/* Card skeletons */}
        <VStack gap={8} align="stretch">
          {[1, 2, 3].map((i) => (
            <Box key={i}>
              <Box display="flex" alignItems="center" gap={3} mb={4}>
                <Box w="48px" h="48px" borderRadius="md" bg={skeletonBg} style={{ ...pulseAnim, animationDelay: `${i * 0.15}s` }} />
                <Box w="200px" h="24px" borderRadius="sm" bg={skeletonBg} style={{ ...pulseAnim, animationDelay: `${i * 0.15}s` }} />
              </Box>
              <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
                {[1, 2, 3].map((j) => (
                  <Box key={j} p={6} borderRadius="lg" bg={skeletonBg} height="120px"
                    style={{ ...pulseAnim, animationDelay: `${(i + j) * 0.12}s` }} />
                ))}
              </Box>
            </Box>
          ))}
        </VStack>
      </Box>
    </Box>
  );
}

// Smart suspense: shows HomeSkeleton for homepage routes, RouteLoader for everything else
function SmartSuspense({ children }) {
  const location = useLocation();
  const isHomepage = location.pathname === '/' || location.pathname === '/tests' || location.pathname === '/test-list';
  return (
    <Suspense fallback={isHomepage ? <HomeSkeleton /> : <RouteLoader />}>
      {children}
    </Suspense>
  );
}

function AppLayout() {
  const { user, isAdmin, canManageQuestions } = useAuthContext();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobileRaw = useBreakpointValue({ base: true, md: false });
  const isMobile = isMobileRaw === undefined ? false : isMobileRaw;

  // Universal background color for light/dark mode
  const universalBg = useColorModeValue('gray.100', 'gray.900');

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    getDoc(doc(db, "users", user.uid)).then((docSnap) => {
      setProfile(docSnap.exists() ? docSnap.data() : null);
      setProfileLoading(false);
    });
  }, [user]);

  useEffect(() => {
    // Set localStorage isAdmin flag based on profile
    if (user && profile && typeof profile.admin === 'boolean') {
      localStorage.setItem('isAdmin', profile.admin ? 'true' : 'false');
    } else if (!user) {
      localStorage.removeItem('isAdmin');
    }
  }, [user, profile]);

  // Cookie utility functions for admin status
  const setCookie = (name, value, days = 30) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  // Expose cookie functions globally for debugging
  useEffect(() => {
    window.clearAdminCookie = () => {
      setCookie('isAdmin', 'false', -1);
      console.log('[Debug] Admin cookie cleared');
    };
    window.getAdminCookie = () => {
      const cookie = getCookie('isAdmin');
      console.log('[Debug] Admin cookie value:', cookie);
      return cookie;
    };
    window.checkAdminStatus = () => {
      console.log('[Debug] Admin Status Check:', {
        isAdmin,
        profileAdmin: profile?.admin,
        localStorageAdmin: localStorage.getItem('isAdmin'),
        adminCookie: getCookie('isAdmin'),
        user: user?.uid,
        pathname: location.pathname
      });
    };
    window.forceAdminCookie = () => {
      setCookie('isAdmin', 'true', 30);
      console.log('[Debug] Admin cookie forced to true');
    };
  }, [isAdmin, profile, user, location.pathname]);

  // Set admin cookie when admin status is detected
  useEffect(() => {
    if (isAdmin || (profile && profile.admin === true)) {
      setCookie('isAdmin', 'true', 30); // 30 days expiry
      console.log('[Analytics] Admin cookie set');
    } else if (!user) {
      // Clear admin cookie when user logs out
      setCookie('isAdmin', 'false', -1); // Expire immediately
    }
  }, [isAdmin, profile, user]);

  // --- Analytics logging on first visit ---
  useEffect(() => {
    // Completely skip analytics for admin users
    if (isAdmin || (profile && profile.admin === true)) {
      console.log('[Analytics] COMPLETELY DISABLED - Admin user detected at component level');
      return;
    }
    
    // Only log on first load and if not admin
    if (window.__analyticsLogged) return;
    
    // Comprehensive admin status check with detailed logging
    const adminCookie = getCookie('isAdmin');
    const isUserAdmin = isAdmin || (profile && profile.admin === true) || (localStorage.getItem('isAdmin') === 'true');
    const isOnAdminRoute = location.pathname.includes('/edit-questions') || 
                           location.pathname.includes('/exams') || 
                           location.pathname.includes('/exam-generator') || 
                           location.pathname.includes('/landing-page') ||
                           location.pathname.includes('/function-log');
    
    console.log('[Analytics Debug]', {
      adminCookie,
      isAdmin,
      profileAdmin: profile?.admin,
      localStorageAdmin: localStorage.getItem('isAdmin'),
      isUserAdmin,
      isOnAdminRoute,
      pathname: location.pathname,
      user: user?.uid
    });
    
    // Block analytics if user is admin or on admin route
    if (adminCookie === 'true' || isUserAdmin || isOnAdminRoute) {
      console.log('[Analytics] BLOCKED - Admin user detected');
      window.__analyticsLogged = true; // Mark as logged to prevent future attempts
      return;
    }
    
    // Additional safety check - if user has admin privileges, don't log
    if (user && (isAdmin || profile?.admin)) {
      console.log('[Analytics] BLOCKED - User has admin privileges');
      window.__analyticsLogged = true;
      return;
    }
    
    console.log('[Analytics] Proceeding with analytics logging for non-admin user');
    window.__analyticsLogged = true;
    
    // Use geo-IP API to get country/countryCode
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        const country = data.country_name || 'Unknown';
        const countryCode = data.country_code || 'UN';
        // Determine referer type
        let refererType = 'direct';
        let refererDetail = 'direct';
        const ref = document.referrer;
        if (ref) {
          const refLower = ref.toLowerCase();
          if (refLower.includes('google') || refLower.includes('bing') || refLower.includes('duckduckgo') || refLower.includes('yahoo')) {
            refererType = 'search';
            if (refLower.includes('google')) refererDetail = 'Google';
            else if (refLower.includes('bing')) refererDetail = 'Bing';
            else if (refLower.includes('duckduckgo')) refererDetail = 'DuckDuckGo';
            else if (refLower.includes('yahoo')) refererDetail = 'Yahoo';
            else refererDetail = 'Other Search';
          } else {
            refererType = 'referral';
            refererDetail = ref;
          }
        }
        // Write to Firestore analytics collection
        const analyticsData = {
          timestamp: serverTimestamp(),
          country,
          countryCode,
          refererType,
          refererDetail,
          userAgent: navigator.userAgent
        };
        if (user && user.uid) analyticsData.userId = user.uid;
        addDoc(collection(db, 'analytics'), analyticsData).catch(() => {}); // Ignore errors
      })
      .catch(() => {}); // Ignore errors
  }, [user, profile, profileLoading, isAdmin, location.pathname]);

  // Logout function
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  // Determine activePath for Sidebar
  const activePath = location.pathname;
  const isPublicBrowseRoute = activePath === "/" || activePath === "/tests" || activePath === "/test-list";
  const showSidebar = !!user && !isPublicBrowseRoute;
  const sidebarAdmin = isAdmin || profile?.admin === true || profile?.role === "admin" || profile?.userType === "admin";
  const sidebarQuestionEditor = canManageQuestions || profile?.role === "question_editor" || profile?.userType === "question_editor";

  return (
    // Universal background color applied to main Flex container
    <Flex direction="column" minHeight="100vh" bg={universalBg}>
      <Header user={user} profile={profile} onOpenSidebar={onOpen} />
      <ScrollToTop />
      {/* Universal background color applied to content Flex */}
      <Flex flex={1} bg={universalBg}>
        {showSidebar && (
            <Sidebar
              activePath={activePath}
              isAdmin={sidebarAdmin}
              canManageQuestions={sidebarQuestionEditor}
              user={user}
              profile={profile}
            loading={profileLoading}
            onLogout={handleLogout}
            isMobile={isMobile}
            isOpen={isOpen}
            onClose={onClose}
          />
        )}
        {/* Universal background color applied to main content Box */}
        <Box as="main" flex={1} overflowY="auto" bg={universalBg}>
          <SmartSuspense>
            <Routes>
              <Route path="/" element={<TestList />} />
              <Route path="/exam-mode/questions/:questionSetId" element={<ExamModeSelect />} />
              <Route path="/exam-mode/custom" element={<ExamModeSelect />} />
              <Route path="/questions/:questionSetId" element={<Questions />} />
              <Route path="/questions/custom" element={<Questions />} />
              <Route path="/questions" element={<Navigate to="/tests" replace />} />
              <Route path="/tests" element={<TestList />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/my-tests" element={<MyTestList />} />
              <Route path="/results/:attemptId" element={<TestResults />} />
              <Route path="/custom-exam/:examId" element={<CustomizingExam />} />
              <Route path="/exams" element={<Exams />} />
              <Route path="/landing-page" element={<LandingPageManager />} />
              <Route path="/admin-users" element={<AdminUsers />} />
              <Route path="/rag-system" element={<RagSystem />} />
              <Route path="/pbq-review" element={<PBQReview />} />
              <Route path="/admin-help" element={<AdminHelp />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/register" element={<UserRegistration />} />
              <Route path="/login" element={<Login onLoginSuccess={() => navigate('/tests')} />} />
              <Route path="/reset-password" element={<PasswordReset />} />
              <Route path="/edit-questions" element={<EditQuestions />} />
              <Route path="/test-list" element={<TestList />} />
              <Route path="/permission-error" element={<PermissionError />} />
              <Route path="/exam-generator" element={<ExamGenerator />} />
              <Route path="/payments" element={<Payment />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/subscriptions" element={<Subscriptions />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/simple-checkout" element={<SimpleCheckout />} />
              <Route path="/payment-link" element={<PaymentLinkCheckout />} />
              {isAdmin && (
                <>
                  <Route path="/edit-questions" element={<EditQuestions />} />
                  <Route path="/function-log" element={<FunctionLog />} />
                </>
              )}
              <Route path="/edit-questions/:questionSetId" element={<EditSingleQuestions />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/drag-demo" element={<DragAndDropMatch />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </SmartSuspense>
        </Box>
      </Flex>
      <Footer />
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
    </Flex>
  );
}

function App() {
  return (
    <Provider system={system}>
      <Toaster />
      <AuthProvider>
        <ShoppingCartProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppLayout />
          </Router>
        </ShoppingCartProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
